import { css, cx } from '@linaria/core'
import { Fragment, type HTMLProps, type ReactNode, useId, useMemo, useState } from 'react'
import { fragments, style } from '#styles'
import { HoverTooltip } from './HoverTooltip'

const segmentedButtonClass = css` 
  width: max-content;
  max-width: 100%;
  border: 2px solid ${style.theme.backgroundSurfaceStandardSubtle};
  background-color: ${style.theme.backgroundSurfaceStandardSubtle};
  display: grid;
  grid-auto-columns: minmax(min-content, 1fr);
  grid-auto-flow: column;
  align-items: stretch;
  
  &._kind-m {
    min-height: 28px;
  }

  &._kind-l {
    min-height: 44px;
  }

  > ._button {
    flex: 1;
    cursor: pointer;
    display: inline-block;
    padding: 0 24px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align: center;
    line-height: 100%;

    color: color-mix(in srgb, ${style.theme.textDefault}, 20% transparent);
    background-color: ${style.theme.backgroundSurfaceStandardSubtle};
    border: none;
    transition: 0.25s ease color, 0.25s ease all;
    ${fragments.textStyle.body.s.condensedBold};

    &:hover {
      color: ${style.theme.textStrong}
      transition: 0.25s ease color, 0.25s ease all;
    }

    &:focus-visible {
      outline: 2px solid ${style.theme.borderOutline};
      outline-offset: -1px;
    }

    &._selected {
      color: ${style.theme.textDefault};
      background: ${style.theme.backgroundSurfacePrimary};
    }


    &:disabled {
      cursor: not-allowed;
      color: ${style.theme.textSoft};
    }
  }

  &._slim {
    > ._button {
      padding: 0 8px;
    }
  }

  &._kind-grid {
    display: grid;
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
    min-height: 44px;
  }
`

// base option type
export type SegmentedButtonOption<Id extends string = string> = {
  id: Id
  content: ReactNode
  title?: string
  disabled?: boolean
  'data-testid'?: string
}

// generic in place to transport brands and such
export interface SegmentedButtonBaseProps<Id extends string = string> {
  options: SegmentedButtonOption<Id>[]
  kind?: 'm' | 'l' | 'grid'
  multiple?: boolean
  slim?: boolean
  'data-testid'?: string
}

interface SegmentedButtonSingleProps<Id extends string = string> extends SegmentedButtonBaseProps<Id> {
  onSelectionChange?: (selection: Id) => unknown
  initialSelection?: Id
  selection?: Id
  multiple?: false
}

interface SegmentedButtonMultiProps<Id extends string = string> extends SegmentedButtonBaseProps<Id> {
  onSelectionChange?: (selections: Id[]) => unknown
  initialSelection?: Id[]
  selection?: Id[]
  multiple: true
}

export type SegmentedButtonProps<Id extends string = string> = SegmentedButtonSingleProps<Id> | SegmentedButtonMultiProps<Id>

export const SegmentedButton = <Id extends string = string>({
  options,
  onSelectionChange,
  initialSelection,
  selection,
  multiple,
  kind = 'm',
  slim,
  className,
  'data-testid': dataTestid,
  ...divProps
}: SegmentedButtonProps<Id> & HTMLProps<HTMLDivElement>): ReactNode => {
  const [localMultiSelection, setLocalMultiSelection] = useState<Id[]>(initialSelection ? (multiple ? initialSelection : [initialSelection]) : [])
  const arraySelection = useMemo(() => (selection ? (multiple ? selection : [selection]) : undefined), [selection, multiple])
  const multiSelection = arraySelection ?? localMultiSelection
  const id = useId()

  return (
    <div id={id} className={cx(segmentedButtonClass, `_kind-${kind}`, slim && '_slim', className)} {...divProps} data-testid={dataTestid}>
      {options.map((x) => {
        const selected = multiSelection.includes(x.id)
        const props = {
          type: 'button',
          title: x.title,
          className: cx('_button', selected && '_selected'),
          disabled: x.disabled,
          onClick: () => {
            if (x.disabled) {
              return
            }
            // discern between the multi and single handling, depending on that, only one selection or all selections are allowed
            if (multiple) {
              const result = selected ? multiSelection.filter((y) => y !== x.id) : [...multiSelection, x.id]
              setLocalMultiSelection(result)
              onSelectionChange?.(result)
            } else {
              setLocalMultiSelection([x.id])
              onSelectionChange?.(x.id)
            }
          },
          'data-testid': x['data-testid'],
        } as const

        return (
          <Fragment key={x.id}>
            {x.title ? <HoverTooltip.button {...props}>{x.content}</HoverTooltip.button> : <button {...props}>{x.content}</button>}
          </Fragment>
        )
      })}
    </div>
  )
}
